// The RFP's Documents tab (Lane 1440, Ryan's mockup documents-mockup.html; only while
// SOURCING_INVITATIONS_ENABLED is on, else DocumentsTab.js renders exactly as before).
//
// One table: the document (type tile and the name suppliers see), type, size, when it was
// added, who sees it (All suppliers, N suppliers, Only my team), then a download button
// and a menu (Edit name or who sees it, Replace file, Remove). Wingman's bid guide is the
// fixed first row. Upload opens one dialog: the file, the name suppliers see, who should
// see it, and after launch "Do suppliers need to re-check their prices because of this
// file?" (No = for information only; the change's policies follow it as before).
//
// After launch every document edit goes into a change (the server's rule); the tab starts
// one when none is in progress, as adding parts does, and suppliers see it only when the
// buyer publishes the change.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatDueAt } from '../sourcing-portal/portalView';
import { STALE_MESSAGE, formatDate } from './sourcingApi';
import { saveBlob, sizeText } from './DocumentsTab';
import useDialogFocus from './useDialogFocus';
import './invitations.css';

const ACCEPT = '.pdf,.xlsx,.docx,.png,.jpg,.jpeg';

const TYPE_SHORT = {
  'application/pdf': ['PDF', 'pdf'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['XLSX', 'xls'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['DOCX', 'doc'],
  'image/png': ['PNG', 'img'],
  'image/jpeg': ['JPG', 'img'],
};
function typeOf(contentType) {
  return TYPE_SHORT[contentType] || ['FILE', 'doc'];
}
function typeOfFile(file) {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  return { pdf: ['PDF', 'pdf'], xlsx: ['XLSX', 'xls'], docx: ['DOCX', 'doc'], png: ['PNG', 'img'],
    jpg: ['JPG', 'img'], jpeg: ['JPG', 'img'] }[ext] || ['FILE', 'doc'];
}
// The name suppliers see, prefilled from the file name without its extension.
function nameFromFile(file) {
  const name = file.name.replace(/\.[^.]+$/, '').trim();
  return (name || file.name).slice(0, 255);
}

// The revision and version a dialog's values are read at: those of the list on screen,
// and only while that list is the one of the revision the page shows (Codex r3). null
// while the list is still loading after a change: nothing can be edited from old rows.
export function editPin(data, summary) {
  if (!data || data.revision !== summary.revision) return null;
  return { revision: data.revision, amendment: data.which === 'amendment' };
}

function FileTile({ type }) {
  return <span className={`srce-dx-tile srce-dx-tile--${type[1]}`} aria-hidden="true">{type[0]}</span>;
}

const DOWNLOAD_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true"><path d="M12 4v11M7 10l5 5 5-5M5 20h14" /></svg>
);
const MORE_ICON = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></svg>
);

function WhoSees({ d }) {
  if (d.audience === 'team') return <span className="srce-dx-who srce-dx-who--team">Only my team</span>;
  if (d.audience === 'some') {
    const n = (d.supplier_ids || []).length;
    const names = (d.suppliers || []).filter(Boolean).join(', ');
    return (
      <span className={`srce-dx-who ${n === 0 ? 'srce-dx-who--none' : ''}`}
        title={n === 0 ? 'No supplier is picked: nobody sees it' : names}>
        {n} supplier{n === 1 ? '' : 's'}
      </span>
    );
  }
  return <span className="srce-dx-who srce-dx-who--all">All suppliers</span>;
}

// One radio card ("All suppliers", "Yes", ...).
function Choice({ name, value, checked, onChange, title, text, children }) {
  return (
    <>
      <label className={`srce-dx-opt ${checked ? 'srce-dx-opt--on' : ''}`}>
        <input type="radio" name={name} value={value} checked={checked} onChange={() => onChange(value)} />
        <span><span className="srce-strong">{title}</span><span className="srce-dx-opt-s">{text}</span></span>
      </label>
      {children}
    </>
  );
}

// The upload, edit and replace dialog. mode: 'add' (file, name, who, re-check after
// launch), 'edit' (the same without the file), 'replace' (the new file and, after launch
// for a document suppliers see, the re-check question only).
function DocumentDialog({ mode, doc, file, suppliers, launched, busy, error, onChooseFile, onSave, onClose }) {
  const box = useDialogFocus(busy ? () => {} : onClose);
  const [name, setName] = useState(() => (mode === 'edit' ? (doc.display_name || doc.file_name) : (file ? nameFromFile(file) : '')));
  const [who, setWho] = useState(() => (doc ? doc.audience : 'all'));
  const [picked, setPicked] = useState(() => new Set(doc && doc.audience === 'some' ? doc.supplier_ids : []));
  const [recheck, setRecheck] = useState(null);
  // A new file chosen in the dialog renames the default (never a name the buyer typed).
  const typed = useRef(false);
  useEffect(() => {
    if (mode === 'add' && file && !typed.current) setName(nameFromFile(file));
  }, [mode, file]);
  const effectiveWho = mode === 'replace' ? doc.audience : who;
  const asksRecheck = launched && effectiveWho !== 'team';
  const someOk = effectiveWho !== 'some' || mode === 'replace' || picked.size > 0;
  const nameOk = mode === 'replace' || name.trim().length > 0;
  const ready = !busy && (mode !== 'add' || file) && someOk && nameOk && (!asksRecheck || recheck !== null);
  const toggle = (id) => setPicked((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const title = { add: 'Upload a document', edit: 'Edit document', replace: 'Replace file' }[mode];
  const save = () => {
    const values = {};
    if (mode !== 'replace') {
      values.display_name = name.trim();
      values.audience = who;
      if (who === 'some') values.supplier_ids = suppliers.map((s) => s.supplier_id).filter((id) => picked.has(id));
    } else {
      // The new file keeps the name suppliers see.
      values.display_name = doc.display_name || doc.file_name;
    }
    if (asksRecheck) values.informational = recheck === 'no';
    onSave(values);
  };
  return (
    <div className="srce-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="srce-modal srce-modal--narrow srce-dx-modal" role="dialog" aria-modal="true" aria-label={title} ref={box}
        data-testid="srce-dx-dialog">
        <div className="srce-card2-h"><span className="srce-card2-t">{title}</span></div>
        <div className="srce-modal-b">
          {error && <div className="srce-error" role="alert">{error}</div>}
          {mode !== 'edit' && (
            <div className="srce-dx-file">
              {file ? <FileTile type={typeOfFile(file)} /> : <FileTile type={['FILE', 'doc']} />}
              <span className="srce-dx-file-n">
                <span className="srce-strong srce-dx-ell" title={file ? file.name : ''}>{file ? file.name : 'No file chosen'}</span>
                {file && <span className="srce-mut">{sizeText(file.size)}</span>}
              </span>
              <button type="button" className="srce-btn srce-btn--quiet" disabled={busy} onClick={onChooseFile}>
                {file ? 'Choose another file' : 'Choose a file'}</button>
            </div>
          )}
          {mode === 'replace' && (
            <div className="srce-mut">The name suppliers see and who sees it stay the same.</div>
          )}
          {mode !== 'replace' && (
            <>
              <label className="srce-dx-lbl" htmlFor="srce-dx-name">Name suppliers see</label>
              <input id="srce-dx-name" className="srce-input srce-dx-inp" value={name} maxLength={255} disabled={busy}
                onChange={(e) => { typed.current = true; setName(e.target.value); }} />
              <div className="srce-dx-lbl" id="srce-dx-who">Who should see it?</div>
              <div role="radiogroup" aria-labelledby="srce-dx-who">
                <Choice name="srce-dx-who" value="all" checked={who === 'all'} onChange={setWho} title="All suppliers"
                  text="Every supplier in this RFP, including ones you add later." />
                <Choice name="srce-dx-who" value="some" checked={who === 'some'} onChange={setWho} title="Only some suppliers"
                  text="Pick who gets it, for example a drawing only for the suppliers quoting that part.">
                  {who === 'some' && (
                    <div className="srce-dx-sups" role="group" aria-label="Suppliers who see it">
                      {suppliers.length === 0 && <div className="srce-mut srce-dx-sup">Add suppliers to the RFP first.</div>}
                      {suppliers.map((s) => (
                        <label key={s.supplier_id} className="srce-dx-sup">
                          <input type="checkbox" checked={picked.has(s.supplier_id)} disabled={busy}
                            onChange={() => toggle(s.supplier_id)} />
                          <span className="srce-dx-ell">{s.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </Choice>
                <Choice name="srce-dx-who" value="team" checked={who === 'team'} onChange={setWho} title="Only my team"
                  text="Kept with the RFP for your team. Suppliers never see it." />
              </div>
            </>
          )}
          {asksRecheck && (
            <>
              <div className="srce-dx-lbl" id="srce-dx-rc">Do suppliers need to re-check their prices because of this file?</div>
              <div className="srce-dx-yn" role="radiogroup" aria-labelledby="srce-dx-rc">
                <Choice name="srce-dx-rc" value="yes" checked={recheck === 'yes'} onChange={setRecheck} title="Yes"
                  text="It changes what they quote. They must confirm their prices again." />
                <Choice name="srce-dx-rc" value="no" checked={recheck === 'no'} onChange={setRecheck} title="No"
                  text="For information only. Their prices stand." />
              </div>
            </>
          )}
        </div>
        <div className="srce-modal-f">
          <span className="srce-mut">{effectiveWho === 'some' && mode !== 'replace' ? `${picked.size} of ${suppliers.length} suppliers` : ''}</span>
          <span className="srce-push srce-row">
            <button type="button" className="srce-btn" disabled={busy} onClick={onClose}>Cancel</button>
            <button type="button" className="srce-btn srce-btn--primary" disabled={!ready} onClick={save}>
              {busy ? 'Saving…' : mode === 'add' ? 'Add document' : mode === 'replace' ? 'Replace file' : 'Save'}</button>
          </span>
        </div>
      </div>
    </div>
  );
}

export default function RfpDocuments({ api, eventId, summary, onChanged }) {
  const live = ['open', 'closed'].includes(summary.status);
  const amending = live && Boolean(summary.amendment);
  const editable = summary.status === 'draft' || live;
  const version = amending ? 'amendment' : 'published';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(null);
  const [dialog, setDialog] = useState(null);     // {mode, doc, file, error}
  const fileRef = useRef(null);
  const pendingPick = useRef(null);               // what the file picker is for

  // Only the newest list request may fill the table: after a change starts, a slower
  // answer for the published version must not replace the change's list (Codex r1).
  const loadSeq = useRef(0);
  const load = useCallback(() => {
    const seq = ++loadSeq.current;
    return api.documents(eventId, version)
      .then((r) => { if (seq === loadSeq.current) setData({ ...r, which: version }); })
      .catch((e) => { if (seq === loadSeq.current) setError(e.message); });
  }, [api, eventId, version]);
  useEffect(() => { load(); }, [load, summary.revision]);
  useEffect(() => {
    if (menu === null) return undefined;
    const close = (e) => { if (!e.target.closest || !e.target.closest('.srce-dx-acts')) setMenu(null); };
    const key = (e) => { if (e.key === 'Escape') setMenu(null); };
    // The menu is placed on the screen (position: fixed) so the table's scroll box never
    // cuts it off. Moving its button would leave it behind, so a resize, or a scroll of a box
    // that holds the button, closes it; other scrolling (the chat panel) leaves it open.
    const away = () => setMenu(null);
    const scrolled = (e) => {
      const btn = document.querySelector('.srce-dx-ib[aria-expanded="true"]');
      const t = e.target;
      if (!btn || t === document || (t && t.contains && t.contains(btn))) setMenu(null);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', key);
    window.addEventListener('scroll', scrolled, true);
    window.addEventListener('resize', away);
    return () => {
      document.removeEventListener('mousedown', close); document.removeEventListener('keydown', key);
      window.removeEventListener('scroll', scrolled, true); window.removeEventListener('resize', away);
    };
  }, [menu]);

  // The revision the buyer's screen shows: an edit made on an older screen is refused
  // (stale_revision) rather than undoing someone else's change (Codex r1). After launch,
  // document edits go into a change: start one from this same revision when none is in
  // progress (as adding parts does), then edit at the revision the start returned.
  // A dialog keeps the revision of the list it was opened from (Codex r2, r3): its
  // values were read at that revision, so a save after anything changed is refused.
  const pin = editPin(data, summary);
  const editRevision = async (opened) => {
    if (!live || opened.amendment) return { revision: opened.revision, started: null };
    const started = await api.amendmentAction(eventId, 'start', {}, opened.revision);
    return { revision: started.revision, started: { revision: started.revision, amendment: true } };
  };

  const run = async (fn, done, opened = pin) => {
    if (!opened) {        // opened while the list was still loading: read it again
      setDialog(null);
      setError(STALE_MESSAGE);
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    let started = null;
    try {
      const at = await editRevision(opened);
      started = at.started;
      await fn(at.revision);
      setDialog(null);
      setNotice(done);
      // The new revision reloads the list of the right version (the effect above).
      await onChanged();
    } catch (e) {
      if (e.code === 'stale_revision') {
        // Someone changed the RFP: the dialog's values are old. Close it; the list
        // reloads and the buyer opens it again on what is there now.
        setDialog(null);
        setError(STALE_MESSAGE);
        await onChanged();
      } else {
        // A change this run started stays started: the dialog continues from it, and
        // the page shows it (Codex r4: a retry must not be refused as stale).
        if (dialog) setDialog((d) => (d ? { ...d, error: e.message, ...(started ? { pin: started } : {}) } : d));
        else setError(e.message);
        if (started) await onChanged();
      }
    } finally {
      setBusy(false);
    }
  };

  const items = (data && data.items) || [];
  const suppliers = (data && data.suppliers) || [];
  const guide = summary.bid_guide || null;
  const full = data && items.length >= data.max_documents;
  const afterNote = live ? ' Suppliers see it when you publish the change.' : '';

  // The picker keeps the pin of the screen it was opened from (Codex r4).
  const pickFile = (purpose) => {
    pendingPick.current = { ...purpose, pin: purpose.pin || pin };
    if (fileRef.current) { fileRef.current.value = ''; fileRef.current.click(); }
  };
  const onFile = (file) => {
    const purpose = pendingPick.current;
    if (!file || !purpose) return;
    if (purpose.mode === 'replace' && !live) {
      // Before launch a replacement keeps the name and who sees it: no question to ask.
      run((rev) => api.replaceDocument(eventId, purpose.doc.document_id, file, rev,
        { display_name: purpose.doc.display_name || purpose.doc.file_name }),
      `Replaced ${purpose.doc.display_name || purpose.doc.file_name}.`, purpose.pin);
      return;
    }
    // "Choose another file" keeps the pin the dialog was opened on.
    setDialog((d) => ({ pin: purpose.pin, ...(d || {}), mode: purpose.mode, doc: purpose.doc || null, file, error: '' }));
  };

  const saveDialog = (values) => {
    const d = dialog;
    if (d.mode === 'add') {
      run((rev) => api.uploadDocument(eventId, d.file, rev, {}, values),
        `Added ${values.display_name}.${afterNote}`, d.pin);
    } else if (d.mode === 'replace') {
      run((rev) => api.replaceDocument(eventId, d.doc.document_id, d.file, rev, values),
        `Replaced ${d.doc.display_name || d.doc.file_name}.${afterNote}`, d.pin);
    } else {
      run((rev) => api.documentAction(eventId, d.doc.document_id, 'audience', values, rev),
        `Saved ${values.display_name}.${afterNote}`, d.pin);
    }
  };
  const remove = (d) => run((rev) => api.documentAction(eventId, d.document_id, 'remove', {}, rev),
    `Removed ${d.display_name || d.file_name}.${live && d.audience !== 'team' ? ' Suppliers are told it is gone when you publish the change.' : ''}`);
  const download = (d) => api.downloadDocument(eventId, d.document_id, version)
    .then((b) => saveBlob(b, d.file_name)).catch((e) => setError(e.message));
  const downloadGuide = () => api.bidGuide(eventId).then((b) => saveBlob(b, guide.file_name))
    .catch((e) => setError(e.message));

  const forSuppliers = items.filter((d) => d.audience !== 'team').length + (guide ? 1 : 0);
  const forTeam = items.filter((d) => d.audience === 'team').length;
  return (
    <section className="srce-card2" data-testid="srce-documents">
      <div className="srce-card2-h srce-docs-h">
        <span className="srce-card2-t">Documents</span>
        {data && <span className="srce-mut srce-docs-note">{forSuppliers} for suppliers, {forTeam} for your team</span>}
        {editable && (
          <span className="srce-push">
            <button type="button" className="srce-btn srce-btn--primary" disabled={busy || full || !pin}
              onClick={() => pickFile({ mode: 'add' })}>Upload a document</button>
          </span>
        )}
        <input ref={fileRef} type="file" accept={ACCEPT} hidden data-testid="srce-dx-file-input"
          onChange={(e) => onFile(e.target.files[0])} />
      </div>
      {amending && <div className="srce-mut srce-card2-f">You are editing the change in progress. Suppliers see these documents after you publish the change.</div>}
      {live && !amending && <div className="srce-mut srce-card2-f">A document you add or change after launch goes into a change. Suppliers see it after you publish the change.</div>}
      {!editable && <div className="srce-mut srce-card2-f">This RFP is finished; its documents are kept here to read.</div>}
      {error && <div className="srce-error" role="alert">{error}</div>}
      {notice && <div className="srce-mut srce-card2-f" role="status">{notice}</div>}
      {full && <div className="srce-mut srce-card2-f">This RFP has the most documents it can hold.</div>}
      <div className="srce-dx-scroll">
        <table className="srce-dx-table" aria-label="Documents">
          <thead>
            <tr>
              <th className="srce-dx-c-doc">Document</th><th className="srce-dx-c-type">Type</th>
              <th className="srce-dx-c-size">Size</th><th className="srce-dx-c-added">Added</th>
              <th className="srce-dx-c-who">Who sees it</th><th className="srce-dx-c-act"><span className="srce-sr">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {guide && (
              <tr data-testid="srce-docs-guide">
                <td><span className="srce-dx-doc"><FileTile type={['PDF', 'pdf']} />
                  <span className="srce-dx-ell" title={guide.title}>{guide.title}</span>
                  <span className="srce-dx-by">from Wingman</span></span></td>
                <td>PDF</td>
                <td className="srce-dx-num">{sizeText(guide.size_bytes)}</td>
                <td className="srce-mut">-</td>
                <td><span className="srce-dx-who srce-dx-who--all">All suppliers</span></td>
                <td className="srce-dx-c-act"><span className="srce-dx-acts">
                  <button type="button" className="srce-dx-ib" title={`Download ${guide.title}`} aria-label={`Download ${guide.title}`}
                    onClick={downloadGuide}>{DOWNLOAD_ICON}</button>
                  <span className="srce-dx-ib-space" />
                </span></td>
              </tr>
            )}
            {!data && !error && <tr><td colSpan={6} className="ll-loading">Loading documents…</td></tr>}
            {data && items.length === 0 && !guide && <tr><td colSpan={6} className="srce-mut">No documents yet.</td></tr>}
            {items.map((d) => {
              const shown = d.display_name || d.file_name;
              return (
                <tr key={d.document_id} data-testid="srce-dx-row">
                  <td><span className="srce-dx-doc"><FileTile type={typeOf(d.content_type)} />
                    <span className="srce-dx-ell" title={shown === d.file_name ? shown : `${shown} (file: ${d.file_name})`}>{shown}</span></span></td>
                  <td>{typeOf(d.content_type)[0]}</td>
                  <td className="srce-dx-num">{sizeText(d.size_bytes)}</td>
                  <td title={formatDueAt(d.added_at)}>{formatDate(d.added_at)}</td>
                  <td><WhoSees d={d} /></td>
                  <td className="srce-dx-c-act"><span className="srce-dx-acts">
                    <button type="button" className="srce-dx-ib" title={`Download ${shown}`} aria-label={`Download ${shown}`}
                      onClick={() => download(d)}>{DOWNLOAD_ICON}</button>
                    {editable ? (
                      <button type="button" className="srce-dx-ib" title="More" aria-label={`More for ${shown}`}
                        aria-haspopup="menu" aria-expanded={menu?.id === d.document_id} disabled={busy || !pin}
                        onClick={(e) => {
                          const r = e.currentTarget.getBoundingClientRect();
                          // Below the button, or above it when the screen has no room below.
                          const pos = window.innerHeight - r.bottom < 150
                            ? { bottom: window.innerHeight - r.top + 4, right: window.innerWidth - r.right }
                            : { top: r.bottom + 4, right: window.innerWidth - r.right };
                          setMenu((m) => (m?.id === d.document_id ? null : { id: d.document_id, pos }));
                        }}>{MORE_ICON}</button>
                    ) : <span className="srce-dx-ib-space" />}
                    {menu?.id === d.document_id && (
                      <span className="srce-dx-menu" role="menu" style={menu.pos}>
                        <button type="button" role="menuitem" onClick={() => { setMenu(null); setDialog({ mode: 'edit', doc: d, file: null, error: '', pin }); }}>
                          Edit name or who sees it</button>
                        <button type="button" role="menuitem" onClick={() => { setMenu(null); pickFile({ mode: 'replace', doc: d }); }}>
                          Replace file</button>
                        <button type="button" role="menuitem" className="srce-dx-menu-d" onClick={() => { setMenu(null); remove(d); }}>
                          Remove</button>
                      </span>
                    )}
                  </span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {dialog && (dialog.mode === 'edit' || dialog.file) && (
        <DocumentDialog key={`${dialog.mode}-${dialog.doc ? dialog.doc.document_id : 'new'}`}
          mode={dialog.mode} doc={dialog.doc} file={dialog.file} suppliers={suppliers} launched={live}
          busy={busy} error={dialog.error}
          onChooseFile={() => pickFile({ mode: dialog.mode, doc: dialog.doc, pin: dialog.pin })}
          onSave={saveDialog} onClose={() => setDialog(null)} />
      )}
    </section>
  );
}
